import React, { useEffect, useState } from 'react';
import { contactApi } from '../services';
import Icon from './Icon';
import { OUTREACH_STAGE_LABELS } from './utils';
import { formatDateTime, timeAgo } from '../utils/time';
import { ActivityEntry } from './AdminPanel';
import './DashboardView.css';

const ORIGIN_LABELS = { linkedin_export: 'From LinkedIn connections', icp_list: 'From ICP target lists', profile_pdf: 'From LinkedIn PDFs' };

const StatCard = ({ label, value, breakdown, hint }) => (
  <div className="dashboard-stat-card" title={hint}>
    <div className="dashboard-stat-card__value">{value.toLocaleString()}</div>
    <div className="dashboard-stat-card__label">{label}</div>
    {breakdown && breakdown.length > 0 && (
      <ul className="dashboard-stat-card__breakdown">
        {breakdown.map(item => (
          <li key={item.label}><span>{item.label}</span><span>{item.count.toLocaleString()}</span></li>
        ))}
      </ul>
    )}
  </div>
);

/**
 * At-a-glance current state: totals, recent LinkedIn import activity, recent audit activity, and
 * where the Google Calendar integration will eventually slot in - named honestly, not faked.
 */
const DashboardView = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    contactApi.getDashboard()
      .then(result => { if (!cancelled) setData(result); })
      .catch(err => { if (!cancelled) setError(err.message || 'Could not load the dashboard.'); });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="dashboard-view">
      {error && <p className="rm-error" role="alert"><Icon name="alert-triangle" /> {error}</p>}
      {!data && !error && <p className="placeholder-text" role="status"><Icon name="loader" spin /> Loading...</p>}
      {data && (
        <>
          <section className="dashboard-view__totals">
            <StatCard label="Companies" value={data.totals.companies_count} />
            <StatCard
              label="Contacts"
              value={data.totals.contacts_count}
              breakdown={data.totals.contacts_by_origin.map(r => ({
                label: ORIGIN_LABELS[r.origin] || r.origin || 'Unknown origin', count: r.count
              }))}
            />
            <StatCard
              label="Target accounts"
              value={data.totals.target_accounts_count}
              breakdown={data.totals.target_accounts_by_stage.map(r => ({
                label: OUTREACH_STAGE_LABELS[r.stage] || 'No outreach recorded', count: r.count
              }))}
            />
            <StatCard
              label="Pending review"
              value={data.totals.pending_findings_count}
              hint="Enrichment findings proposed, not yet accepted or applied"
            />
          </section>

          <section className="dashboard-view__section">
            <h2>Recent LinkedIn imports</h2>
            {data.recent_imports.length === 0 ? (
              <p className="rm-empty">No completed LinkedIn imports yet.</p>
            ) : (
              <table className="dashboard-table">
                <thead>
                  <tr>
                    <th>Advisor</th>
                    <th>When</th>
                    <th>Added</th>
                    <th>Refreshed</th>
                    <th>Companies created</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_imports.map(job => (
                    <tr key={job.job_id}>
                      <td>{job.advisor || '–'}</td>
                      <td title={formatDateTime(job.finished_at)}>{timeAgo(job.finished_at)}</td>
                      <td>{job.added}</td>
                      <td>{job.refreshed}</td>
                      <td>{job.companies_created}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section className="dashboard-view__section">
            <h2>Recent activity</h2>
            <p className="dashboard-view__activity-counts">
              Last {data.recent_activity.window_days} days: {data.recent_activity.counts['enrichment.applied']} enrichment
              field{data.recent_activity.counts['enrichment.applied'] === 1 ? '' : 's'} applied, {data.recent_activity.counts['profile.saved']} profile{data.recent_activity.counts['profile.saved'] === 1 ? '' : 's'} matched from a PDF.
            </p>
            {data.recent_activity.events.length === 0 ? (
              <p className="rm-empty">No activity logged yet.</p>
            ) : (
              <ul className="adm-events dashboard-view__activity-list">
                {data.recent_activity.events.map(event => <ActivityEntry key={event.id} event={event} />)}
              </ul>
            )}
          </section>

          <section className="dashboard-view__section dashboard-view__coming-soon">
            <h2>Coming soon</h2>
            <div className="dashboard-coming-soon-card">
              <h3>Google Calendar integration</h3>
              <p>Meeting counts and notes/minutes pulled from Calendar aren&apos;t connected yet - this card will show them once they are.</p>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default DashboardView;
